import React, { useEffect, useMemo, useState } from 'react'
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table'
import { Table, TableContainer } from '../commonUI/Table/styles'
import { Pagination } from '../commonUI/Table/Pagination'
import { useDispatch, useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { Avatar, Box, Flex, Text, Tooltip, useToast } from '@chakra-ui/core'
import LocaleText from 'components/LocaleText'
import { DateToLocaleValue } from 'components/ui/DateToLocaleValue'
import { InputWithIcon } from '../commonUI/InputWithIcon'
import { Edit, Search } from 'lucide-react'
import { useUserName } from 'hooks/useUserName'
import useAvatar from 'hooks/useAvatar'
import { NoResults } from 'components/CommonComponents/GroupsTree/NoResults'
import { CustomSkeleton } from 'components/Elements'
import useLocaleText from 'components/useLocaleText'
import dayjs from 'dayjs'
import { usePromptsModals } from './PromtsModalProvider'
import { AppDispatch } from 'config/redux'
import { getPrompt } from 'redux/actions/promts'
import { ActionMenu } from '../commonUI/ActionMenu'

export interface IPresetPrompt {
  content: string
  created_at_ts: number
  created_by: string
  oid: string
  title: string
}

const createColumns = () => [
  {
    Header: () => {
      const t_name = useLocaleText('name', true)
      return (
        <Text>
          <LocaleText text={t_name} />
        </Text>
      )
    },
    accessor: 'title',
    Cell: ({ row }: { row: { original: IPresetPrompt } }) => {
      const title = row.original?.title || 'no title'

      return (
        <Tooltip label={title} placement="bottom" hasArrow aria-label="Prompt title" zIndex={10000}>
          <Box w="max-content">
            <Text fontSize="sm" fontWeight="bold" isTruncated maxW="250px">
              {title}
            </Text>
          </Box>
        </Tooltip>
      )
    },
  },
  {
    Header: () => {
      const t_content = useLocaleText('t_content', true)
      return (
        <Text>
          <LocaleText text={t_content} />
        </Text>
      )
    },
    accessor: 'content',
    Cell: ({ row }: { row: { original: IPresetPrompt } }) => {
      const content = row.original?.content || 'no content'

      return (
        <Tooltip label={content} placement="bottom" hasArrow aria-label="Prompt content" zIndex={10000}>
          <Box w="max-content">
            <Text fontSize="xs" isTruncated maxW="300px">
              {content}
            </Text>
          </Box>
        </Tooltip>
      )
    },
  },
  {
    Header: () => {
      const t_created_by = useLocaleText('jobs_created_by', true)
      return (
        <Text>
          <LocaleText text={t_created_by} />
        </Text>
      )
    },
    accessor: 'created_by',
    Cell: ({ row }: { row: { original: IPresetPrompt } }) => {
      const uid = row.original.created_by
      const fullName = useUserName(uid)
      const avatar = useAvatar(uid)
      return (
        <Flex align="center" style={{ columnGap: '10px' }}>
          <Avatar name={fullName} src={avatar} size="md" />
          <Box>
            <Text maxW="200px" isTruncated fontWeight="medium">
              {fullName}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {uid}
            </Text>
          </Box>
        </Flex>
      )
    },
  },
  {
    Header: () => {
      const t_created_at = useLocaleText('created_at', true)
      return (
        <Text>
          <LocaleText text={t_created_at} />
        </Text>
      )
    },
    accessor: 'created_at_ts',
    Cell: ({ row }: { row: { original: IPresetPrompt } }) => {
      const timestamp = row.original.created_at_ts
      const parsedDate = timestamp ? dayjs(timestamp * 1000).toDate() : null

      return <DateToLocaleValue date={parsedDate} placeholder="N/A" fontSize="14px" fontWeight={500} />
    },
  },
  {
    Header: () => (
      <Text>
        <LocaleText text="admin_actions" />
      </Text>
    ),
    accessor: 'actions',
    Cell: ({ row }: { row: { original: IPresetPrompt } }) => {
      const prompt = row.original
      const dispatch: AppDispatch = useDispatch()
      const toast = useToast()
      const { openPromtsModal } = usePromptsModals()
      const [promptOidLoading, setPromptOidLoading] = useState<string | null>(null)

      const handleGetPrompt = async () => {
        try {
          setPromptOidLoading(prompt.oid)
          const promptText = await dispatch(getPrompt({ prompt_id: prompt.oid }))
          return promptText
        } catch (error) {
          console.error(error)
          return null
        } finally {
          setPromptOidLoading(null)
        }
      }

      const onClick = async () => {
        const promptText = await handleGetPrompt()
        if (!promptText) {
          toast({
            title: 'Error loading prompt',
            description: 'Failed to load prompt',
            status: 'error',
            position: 'top-right',
          })
          return
        }

        openPromtsModal({ ...prompt, prompt: promptText })
      }

      const optionsList = [{ label: 'edit_prompt', icon: <Edit size={18} />, onClick: onClick, color: 'blue.500' }]

      return (
        <Flex w="100%" justifyContent="flex-start">
          <ActionMenu
            isLoading={promptOidLoading === prompt.oid}
            tooltipText="admin_edit_action"
            optionsList={optionsList}
            triggerProps={{ pos: 'relative', top: 0, right: 0 }}
          />
        </Flex>
      )
    },
  },
]

interface PromptsTableProps {
  prompts: IPresetPrompt[]
  loading: boolean
}

const TableSkeleton = () => {
  const isRtl = useSelector(isRtlSelector)

  return (
    <TableContainer columnWidths={{ 2: '300px', 3: '300px' }}>
      <Table columnWidths={{ 2: '300px', 3: '300px' }}>
        <thead>
          <tr>
            {['title', 'content', 'created_by', 'created_at', 'admin_actions'].map((header, index) => (
              <th key={index} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                <Text>
                  <LocaleText text={header} isLocalUse />
                </Text>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: 5 }).map((_, cellIndex) => (
                <td key={cellIndex} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                  <CustomSkeleton
                    height={cellIndex === 0 ? '40px' : '20px'}
                    width={cellIndex === 0 ? '200px' : cellIndex === 1 ? '300px' : cellIndex === 2 ? '200px' : '100px'}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  )
}

export const PromptsTable = ({ prompts, loading }: PromptsTableProps) => {
  const columns = useMemo(() => createColumns(), [])
  const isRtl = useSelector(isRtlSelector)
  const t_search = useLocaleText('search')

  const [searchValue, setSearchValue] = useState('')

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setGlobalFilter,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: prompts,
      initialState: {
        pageIndex: 0,
        pageSize: 12,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  useEffect(() => {
    setGlobalFilter(searchValue)
  }, [searchValue, setGlobalFilter])

  return (
    <>
      <Flex justify="flex-end" align="center" mb={4} flexWrap={{ base: 'wrap', md: 'nowrap' }} style={{ gap: '10px' }}>
        <Box w="100%" maxW="300px">
          <InputWithIcon
            icon={<Search />}
            value={searchValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            borderRadius="5px"
            isDisabled={loading}
            placeholder={t_search}
          />
        </Box>
      </Flex>

      {loading ? (
        <TableSkeleton />
      ) : page.length > 0 ? (
        <TableContainer style={{ maxHeight: 'calc(100vh - 400px)' }} columnWidths={{ 2: '300px', 3: '300px' }}>
          <Table {...getTableProps()} columnWidths={{ 2: '300px', 3: '300px' }}>
            <thead>
              {headerGroups.map((headerGroup: any, index: number) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column: any, index: number) => (
                    <th {...column.getHeaderProps()} key={index} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                      {column.render('Header')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row: any, index: number) => {
                prepareRow(row)
                return (
                  <tr {...row.getRowProps()} key={index}>
                    {row.cells.map((cell: any, index: number) => (
                      <td {...cell.getCellProps()} key={index} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </TableContainer>
      ) : (
        <NoResults />
      )}

      <Pagination
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        previousPage={previousPage}
        nextPage={nextPage}
      />
    </>
  )
}
